import { useState, useEffect } from 'react';
import { useCMSAuth } from '@/context/CMSAuthContext';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Phone,
  Mail,
  MapPin,
  Plus,
  Edit,
  Trash2,
  Loader2,
  MoreHorizontal,
  Building,
  Globe,
  MessageCircle,
  Facebook,
  Instagram,
  Linkedin,
  Youtube,
  Twitter,
  Save
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

export const ContactManager = () => {
  const { token, user, logout } = useCMSAuth();
  const [loading, setLoading] = useState(true);
  const [offices, setOffices] = useState([]);
  const [settings, setSettings] = useState({});
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    city: '',
    address: '',
    phone: '',
    email: '',
    google_maps_url: '',
    business_hours: '',
    is_headquarters: false,
    is_active: true,
    order: 0
  });

  const handleUnauthorized = (res) => {
    if (res.status === 401) {
      toast.error('Session expired. Please log in again.');
      logout();
      return true;
    }
    return false;
  };

  const fetchData = async () => {
    try {
      const [officesRes, settingsRes] = await Promise.all([
        fetch(`${API_URL}/api/cms/content/contact/offices`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/cms/content/contact/settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (handleUnauthorized(officesRes) || handleUnauthorized(settingsRes)) return;
      
      if (officesRes.ok) {
        const data = await officesRes.json();
        setOffices(data.offices);
      }
      if (settingsRes.ok) {
        const data = await settingsRes.json();
        setSettings(data);
      }
    } catch (e) {
      toast.error('Failed to fetch contact data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const handleSaveOffice = async () => {
    if (!formData.name || !formData.city || !formData.address) {
      toast.error('Name, city, and address are required');
      return;
    }
    
    setFormLoading(true);
    try {
      const payload = {
        ...formData,
        order: parseInt(formData.order) || 0
      };
      
      const url = selectedOffice 
        ? `${API_URL}/api/cms/content/contact/offices/${selectedOffice.id}`
        : `${API_URL}/api/cms/content/contact/offices`;
      
      const res = await fetch(url, {
        method: selectedOffice ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success(selectedOffice ? 'Office updated' : 'Office created');
        setDialogOpen(false);
        resetForm();
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save');
      }
    } catch (e) {
      toast.error('Failed to save office');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteOffice = async () => {
    if (!selectedOffice) return;
    
    setFormLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/cms/content/contact/offices/${selectedOffice.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Office deleted');
        setDeleteDialogOpen(false);
        setSelectedOffice(null);
        fetchData();
      } else {
        toast.error('Failed to delete');
      }
    } catch (e) {
      toast.error('Failed to delete office');
    } finally {
      setFormLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSettingsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/cms/content/contact/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      
      if (res.ok) {
        toast.success('Settings saved');
      } else {
        toast.error('Failed to save settings');
      }
    } catch (e) {
      toast.error('Failed to save settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  const openEditDialog = (office) => {
    setSelectedOffice(office);
    setFormData({
      name: office.name,
      city: office.city,
      address: office.address,
      phone: office.phone || '',
      email: office.email || '',
      google_maps_url: office.google_maps_url || '',
      business_hours: office.business_hours || '',
      is_headquarters: office.is_headquarters || false,
      is_active: office.is_active !== false,
      order: office.order?.toString() || '0'
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      city: '',
      address: '',
      phone: '',
      email: '',
      google_maps_url: '',
      business_hours: '',
      is_headquarters: false,
      is_active: true,
      order: offices.length.toString()
    });
    setSelectedOffice(null);
  };

  const canEdit = ['super_admin', 'admin', 'content_approver', 'content_editor'].includes(user?.role);
  const canDelete = ['super_admin', 'admin', 'content_approver'].includes(user?.role);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-ux-cyan" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="contact-manager">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-ux-charcoal flex items-center gap-2">
            <Phone className="w-7 h-7 text-ux-green" />
            Contact Page
          </h1>
          <p className="text-ux-charcoal/60">
            Manage office locations and contact information
          </p>
        </div>

        <Tabs defaultValue="offices" className="space-y-6">
          <TabsList>
            <TabsTrigger value="offices">
              <Building className="w-4 h-4 mr-2" />
              Offices
            </TabsTrigger>
            <TabsTrigger value="settings">
              <Globe className="w-4 h-4 mr-2" />
              Contact Info & Social
            </TabsTrigger>
          </TabsList>

          {/* Offices Tab */}
          <TabsContent value="offices" className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-ux-charcoal/60">
                {offices.length} office location(s)
              </p>
              {canEdit && (
                <Button onClick={() => { resetForm(); setDialogOpen(true); }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Office
                </Button>
              )}
            </div>

            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Office</TableHead>
                      <TableHead>City</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead className="text-center">Active</TableHead>
                      {canEdit && <TableHead className="w-16">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {offices.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-ux-charcoal/60">
                          No offices added yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      offices.map((office) => (
                        <TableRow key={office.id} className={!office.is_active ? 'opacity-50' : ''}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{office.name}</span>
                              {office.is_headquarters && (
                                <Badge variant="outline" className="text-xs">HQ</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{office.city}</TableCell>
                          <TableCell className="text-sm">{office.phone || '-'}</TableCell>
                          <TableCell className="text-sm">{office.email || '-'}</TableCell>
                          <TableCell className="text-center">
                            {office.is_active !== false ? (
                              <Badge className="bg-green-500 text-white">Active</Badge>
                            ) : (
                              <Badge variant="outline">Inactive</Badge>
                            )}
                          </TableCell>
                          {canEdit && (
                            <TableCell>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <MoreHorizontal className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => openEditDialog(office)}>
                                    <Edit className="w-4 h-4 mr-2" /> Edit
                                  </DropdownMenuItem>
                                  {canDelete && (
                                    <DropdownMenuItem 
                                      onClick={() => { setSelectedOffice(office); setDeleteDialogOpen(true); }}
                                      className="text-red-600"
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" /> Delete
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Primary Contact Information</CardTitle>
                <CardDescription>Main contact details displayed on the website</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-2">
                      <Phone className="w-4 h-4" /> Primary Phone
                    </Label>
                    <Input
                      value={settings.primary_phone || ''}
                      onChange={(e) => setSettings(s => ({ ...s, primary_phone: e.target.value }))}
                      placeholder="+91-7273928928"
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2">
                      <MessageCircle className="w-4 h-4" /> WhatsApp Number
                    </Label>
                    <Input
                      value={settings.whatsapp_number || ''}
                      onChange={(e) => setSettings(s => ({ ...s, whatsapp_number: e.target.value }))}
                      placeholder="+917273928928"
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2">
                      <Mail className="w-4 h-4" /> Primary Email
                    </Label>
                    <Input
                      value={settings.primary_email || ''}
                      onChange={(e) => setSettings(s => ({ ...s, primary_email: e.target.value }))}
                      placeholder="info@unixperts.com"
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2">
                      <Mail className="w-4 h-4" /> Support Email
                    </Label>
                    <Input
                      value={settings.support_email || ''}
                      onChange={(e) => setSettings(s => ({ ...s, support_email: e.target.value }))}
                      placeholder="support@unixperts.com"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Social Media Links</CardTitle>
                <CardDescription>Connect your social profiles</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-2">
                      <Facebook className="w-4 h-4" /> Facebook
                    </Label>
                    <Input
                      value={settings.facebook_url || ''}
                      onChange={(e) => setSettings(s => ({ ...s, facebook_url: e.target.value }))}
                      placeholder="https://facebook.com/..."
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2">
                      <Instagram className="w-4 h-4" /> Instagram
                    </Label>
                    <Input
                      value={settings.instagram_url || ''}
                      onChange={(e) => setSettings(s => ({ ...s, instagram_url: e.target.value }))}
                      placeholder="https://instagram.com/..."
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2">
                      <Linkedin className="w-4 h-4" /> LinkedIn
                    </Label>
                    <Input
                      value={settings.linkedin_url || ''}
                      onChange={(e) => setSettings(s => ({ ...s, linkedin_url: e.target.value }))}
                      placeholder="https://linkedin.com/company/..."
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2">
                      <Youtube className="w-4 h-4" /> YouTube
                    </Label>
                    <Input
                      value={settings.youtube_url || ''}
                      onChange={(e) => setSettings(s => ({ ...s, youtube_url: e.target.value }))}
                      placeholder="https://youtube.com/..."
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-2">
                      <Twitter className="w-4 h-4" /> Twitter / X
                    </Label>
                    <Input
                      value={settings.twitter_url || ''}
                      onChange={(e) => setSettings(s => ({ ...s, twitter_url: e.target.value }))}
                      placeholder="https://twitter.com/..."
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {canEdit && (
              <div className="flex justify-end">
                <Button onClick={handleSaveSettings} disabled={settingsLoading}>
                  {settingsLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Settings
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Add/Edit Office Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedOffice ? 'Edit Office' : 'New Office'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Office Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(d => ({ ...d, name: e.target.value }))}
                    placeholder="e.g. Head Office"
                  />
                </div>
                <div>
                  <Label>City *</Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData(d => ({ ...d, city: e.target.value }))}
                    placeholder="e.g. Gurgaon"
                  />
                </div>
              </div>
              <div>
                <Label>Full Address *</Label>
                <Textarea
                  value={formData.address}
                  onChange={(e) => setFormData(d => ({ ...d, address: e.target.value }))}
                  placeholder="Complete address with pin code"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData(d => ({ ...d, phone: e.target.value }))}
                    placeholder="+91-..."
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    value={formData.email}
                    onChange={(e) => setFormData(d => ({ ...d, email: e.target.value }))}
                    placeholder="office@unixperts.com"
                  />
                </div>
              </div>
              <div>
                <Label>Google Maps URL</Label>
                <Input
                  value={formData.google_maps_url}
                  onChange={(e) => setFormData(d => ({ ...d, google_maps_url: e.target.value }))}
                  placeholder="https://maps.google.com/..."
                />
              </div>
              <div>
                <Label>Business Hours</Label>
                <Input
                  value={formData.business_hours}
                  onChange={(e) => setFormData(d => ({ ...d, business_hours: e.target.value }))}
                  placeholder="Mon-Sat: 10AM - 7PM"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Display Order</Label>
                  <Input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData(d => ({ ...d, order: e.target.value }))}
                  />
                </div>
                <div className="flex items-center gap-3 pt-6">
                  <Switch
                    checked={formData.is_headquarters}
                    onCheckedChange={(v) => setFormData(d => ({ ...d, is_headquarters: v }))}
                  />
                  <Label>Headquarters</Label>
                </div>
                <div className="flex items-center gap-3 pt-6">
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(v) => setFormData(d => ({ ...d, is_active: v }))}
                  />
                  <Label>Active</Label>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveOffice} disabled={formLoading}>
                {formLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {selectedOffice ? 'Save Changes' : 'Add Office'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-600">Delete Office</DialogTitle>
            </DialogHeader>
            <p>Are you sure you want to delete "{selectedOffice?.name}"?</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleDeleteOffice} disabled={formLoading}>
                {formLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default ContactManager;
